// uiarc: card (https://uiarc.dev/r/card.json) yüzeyi. uiarc kartı başlık/açıklama/medya/eylem veren tek parçalı
// bileşendir; burada shadcn parçaları (Header/Title/Description/Content/Footer) korunur ve yüzey birebir alınır:
// 1px kenarlık, `panel` yarıçapı, `surface` zemini, 20px iç boşluk, başlık 18px/500, açıklama 14px ikincil renk.
// uiarc kartı her zaman süzülür; burada yalnız tıklanabilir/özet kartlar için `interactive` ile açılır
// (üzerine gelince 2px yükselir, kenarlık güçlenir, `shadow-raised` gelir; dokunmatikte yok, azaltılmış harekette
// yükselme yok). Tıklanabilir olmayan, tablo/grafik taşıyan kartın süzülmesi anlamsız olurdu.
// uiarc'ın "hızlı bakış" (kartın diyaloğa büyümesi) Faz 3'te dialog ile birlikte ele alınır.
import * as React from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

type CardProps = Omit<
  React.ComponentProps<'div'>,
  'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'
> & {
  size?: 'default' | 'sm'
  /** Üzerine gelince süzülür (uiarc kartı); tıklanabilir/özet kartlar için. */
  interactive?: boolean
}

function Card({ className, size = 'default', interactive = false, ...props }: CardProps) {
  const reduced = useReducedMotion() ?? false
  const classes = cn(
    'group/card bg-surface text-card-foreground border-border rounded-panel ease-standard isolate flex flex-col gap-(--card-spacing) overflow-hidden border py-(--card-spacing) text-sm [--card-spacing:--spacing(5)] transition-[border-color,box-shadow] duration-(--duration-fast) has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 data-[size=sm]:[--card-spacing:--spacing(4)] data-[size=sm]:has-data-[slot=card-footer]:pb-0 *:[img:first-child]:rounded-none *:[img:last-child]:rounded-none motion-reduce:transition-none',
    interactive && 'hover:border-border-strong hover:shadow-raised duration-(--duration-standard)',
    className,
  )
  if (!interactive) {
    return <div data-slot="card" data-size={size} className={classes} {...props} />
  }
  return (
    <motion.div
      data-slot="card"
      data-size={size}
      data-interactive=""
      className={classes}
      whileHover={reduced ? undefined : { y: -2 }}
      transition={motionTokens.spring.snappy}
      {...(props as React.ComponentProps<typeof motion.div>)}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-2 px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'font-heading text-foreground w-fit max-w-full text-lg leading-[1.1] font-medium tracking-(--tracking-body) text-balance',
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-text-secondary leading-body text-sm text-pretty', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-content" className={cn('px-(--card-spacing)', className)} {...props} />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('border-border flex items-center border-t p-(--card-spacing)', className)}
      {...props}
    />
  )
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent }
